/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import { useField } from 'informed';
import DateElement, { IDate } from '../../components/Date';
import moment from 'moment';
import './styles.scss';

export interface IDateInformed extends Omit<IDate, 'onChange'> {
  name: string;
  className?: string;
  labelClassName?: string;
  errorClassName?: string;
  label?: string | JSX.Element;
  defaultValue?: Date;
  storeDateFormat?: string;
  onChange?: (value: Date | string) => void;
  validate?: (value: unknown) => void | string;
}

const DateInformed: FC<IDateInformed> = ({
  name,
  defaultValue,
  openToDate,
  placeholder,
  className,
  labelClassName,
  errorClassName,
  dateFormat = 'dd.MM.yyyy',
  storeDateFormat,
  label,
  dropdownMode = 'select',
  showMonthDropdown = true,
  showYearDropdown = true,
  onChange,
  validate,
  ...rest
}) => {
  const { fieldState, fieldApi, render } = useField<IDateInformed, Date>({ name, validate });

  const { error, value } = fieldState;
  const { setValue, setTouched, validate: validateField, reset } = fieldApi;

  const [dateValue, setDateValue] = useState<Date | null>(value instanceof Date ? value : null);
  const selectedValue = dateValue || undefined;

  useEffect(() => {
    if (defaultValue) {
      setDateValue(defaultValue);
      setValue(defaultValue);
    }
  }, []);

  useEffect(() => {
    if (!value) {
      setDateValue(null);
    } else if (value instanceof Date) {
      setDateValue(value);
    }
  }, [value]);

  return render(
    <>
      {label && <label className={cn('hbh-date-label', labelClassName)}>{label}</label>}
      <DateElement
        onFocus={() => setTouched(true)}
        onChange={(date: Date) => {
          const formattedDate = storeDateFormat ? moment(date).format(storeDateFormat) : date;

          if (date) {
            setValue(formattedDate);
            setDateValue(date);
          } else {
            setDateValue(null);
            reset();
          }

          validateField();
          onChange?.(formattedDate);
        }}
        className={cn('hbh-date', className)}
        selected={dateValue}
        shouldCloseOnSelect
        openToDate={openToDate || selectedValue}
        placeholderText={placeholder}
        dateFormatCalendar="MMMM"
        dateFormat={dateFormat}
        dropdownMode={dropdownMode}
        showMonthDropdown={showMonthDropdown}
        showYearDropdown={showYearDropdown}
        {...rest}
      />
      {error ? <p className={cn('hbh-date-error', errorClassName)}>{error as string}</p> : null}
    </>,
  );
};

export default DateInformed;
